import { useRef, useState } from 'react';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DatagridPremiumUi,
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  SelectUI,
  AddButtonUi,
  CheckboxUI,
} from '../Interface';
import { getTableEditableFields } from '../../utils/CompanyHelper';

function EditToolbar(props) {
  const { disabled, push, handleClick } = props;

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(push)} />
    </GridToolbarContainer>
  );
}

const CompanyContact = ({ params, disabled, defaultContact }) => {
  const apiRef = useGridApiRef();
  const focusedFieldRef = useRef(null);
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const handleDefaultChange = (id, checked, dataApi) => {
    const currentIndex = values.contact.findIndex((c) => c.id === id);

    if (checked) {
      values.contact.forEach((contact, index) => {
        if (contact.id !== id && contact.default) {
          setFieldValue(`contact.${index}.default`, false);
          dataApi.setEditCellValue({
            id: contact.id,
            field: 'default',
            value: false,
          });
        }
      });

      setFieldValue(`contact.${currentIndex}.default`, true);
      dataApi.setEditCellValue({
        id: id,
        field: 'default',
        value: true,
      });
    } else {
      setFieldValue(`contact.${currentIndex}.default`, false);
      dataApi.setEditCellValue({
        id: id,
        field: 'default',
        value: false,
      });
    }
  };

  const getValueIndex = (values, id) => {
    return values.contact.findIndex((rc) => rc.id === id);
  };

  const roleOptionParam = params.PROJECT_CONTACT_ROLE_OPTIONS;
  const roleOptions = roleOptionParam ? JSON.parse(roleOptionParam) : [];

  const roleOption = roleOptions && roleOptions.length > 0;

  const focusFirstEditableCell = (rowId) => {
    const editableFields = getTableEditableFields(apiRef);
    const fieldToFocus = editableFields[0] ?? 'name';

    focusedFieldRef.current = fieldToFocus;
    apiRef.current.startRowEditMode({ id: rowId });
    apiRef.current.setCellFocus(rowId, fieldToFocus);
    focusEditorInput(rowId, fieldToFocus);
  };

  const handleClick = async (push) => {
    const newField = {
      ...defaultContact,
      id: uuidv4(),
    };

    await push(newField);
    focusFirstEditableCell(newField.id);

    const fieldsToValidate = [
      `contact.${values.contact.length ?? 0}.name`,
      `contact.${values.contact.length ?? 0}.role`,
      `contact.${values.contact.length ?? 0}.email`,
      `contact.${values.contact.length ?? 0}.phoneNumber`,
      `contact.${values.contact.length ?? 0}.phoneNumber2`,
      `contact.${values.contact.length ?? 0}.comment`,
      `contact.${values.contact.length ?? 0}.default`,
      `contact.${values.contact.length ?? 0}.active`,
      `contact.${values.contact.length ?? 0}.other`,
      `contact.${values.contact.length ?? 0}.salutation`,
      `contact.${values.contact.length ?? 0}.newsletter`,
    ];

    for (const field of fieldsToValidate) {
      validateField(field);
    }

    return newField.id;
  };

  const focusEditorInput = (id, field, { select = true } = {}) => {
    apiRef.current.setCellFocus(id, field);

    requestAnimationFrame(() => {
      const cellEl = apiRef.current.getCellElement?.(id, field);
      if (!cellEl) return;

      const editorInput = cellEl.querySelector('input, textarea, [contenteditable="true"], [tabindex="0"], button');

      if (editorInput) {
        editorInput.focus();
        if (select && typeof editorInput.select === 'function') {
          try {
            editorInput.select();
          } catch {}
        }
      }
    });
  };

  const handleArrowKeyNavigation = async (params, event, push) => {
    const key = event.key;
    const isArrowKey = key === 'ArrowDown' || key === 'ArrowUp';

    if (!isArrowKey || !event.ctrlKey) return;
    event.preventDefault();

    const rowId = params.id;
    if (apiRef.current.getRowMode(rowId) !== GridRowModes.Edit) return;

    apiRef.current.stopRowEditMode({ id: rowId });

    const rowIndex = getValueIndex(values, rowId);
    const offset = key === 'ArrowDown' ? 1 : -1;
    let nextIndex = rowIndex + offset;

    const nextRow = values.contact[nextIndex];

    if (key === 'ArrowDown' && !nextRow) {
      await handleClick(push);
      return;
    }

    if (!nextRow) return;

    if (apiRef.current.getRowMode(nextRow.id) === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: nextRow.id });
    }

    focusedFieldRef.current = params.field;
    focusEditorInput(nextRow.id, params.field);
  };

  const handleTabKeyNavigation = (params, event) => {
    const isTabOrEnter = event.key === 'Tab' || event.key === 'Enter';
    if (!isTabOrEnter) return;

    const rowId = params.id;

    const editableFields = getTableEditableFields(apiRef);

    const currentField = params.field;
    const currentIdx = editableFields.indexOf(currentField);
    const dir = event.shiftKey ? -1 : 1;
    const nextIdx = currentIdx + dir;

    if (nextIdx < 0 || nextIdx >= editableFields.length) return;

    event.preventDefault();
    event.defaultMuiPrevented = true;

    const nextField = editableFields[nextIdx];

    focusedFieldRef.current = nextField;
    focusEditorInput(rowId, nextField);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Név',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.name`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'role',
        headerName: 'Szerep',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              sx={
                roleOption && {
                  height: '19px',
                  '&.MuiInputBase-root': { paddingBottom: '15px' },
                }
              }
              name="role"
              as={roleOption ? SelectUI : InputUI}
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.role} />}
              variant="standard"
              option={roleOptions}
              value={data.row.role ?? ''}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.role`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.role`)}
            />
          );
        },
      },
      {
        field: 'phoneNumber',
        headerName: 'Telefonszám',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.phoneNumber`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.phoneNumber} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.phoneNumber`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.phoneNumber`)}
            />
          );
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.email`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.email} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.email`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.email`)}
            />
          );
        },
      },
      {
        field: 'phoneNumber2',
        headerName: 'Telefonszám2',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.phoneNumber2`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.phoneNumber2} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.phoneNumber2`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.phoneNumber2`)}
            />
          );
        },
      },
      {
        field: 'salutation',
        headerName: 'Megszólítás',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.salutation`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.salutation} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.salutation`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.salutation`)}
            />
          );
        },
      },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`contact.${valueIndex}.comment`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.comment} />}
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.comment`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.comment`)}
            />
          );
        },
      },
      {
        field: 'newsletter',
        type: 'boolean',
        headerName: 'Hírlevél',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`contact.${valueIndex}.newsletter`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.newsletter} />}
              checked={values.contact?.[valueIndex]?.newsletter}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.newsletter`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.newsletter`)}
            />
          );
        },
      },
      {
        field: 'default',
        headerName: 'Alapértelmezett',
        type: 'boolean',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`contact.${valueIndex}.default`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.default} />}
              checked={values.contact?.[valueIndex]?.default}
              variant="standard"
              onChange={(e) => handleDefaultChange(data.id, e.target.checked, data.api)}
              onBlur={async () => await validateField(`contact.${valueIndex}.default`)}
            />
          );
        },
      },
      {
        field: 'active',
        type: 'boolean',
        headerName: 'Aktív',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`contact.${valueIndex}.active`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.contact?.[valueIndex]?.active} />}
              checked={values.contact?.[valueIndex]?.active}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`contact.${valueIndex}.active`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`contact.${valueIndex}.active`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="contact">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.CompanyContact}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.contact ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    handleClick,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                getRowHeight={() => 'auto'}
                editMode="row"
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
                onCellKeyDown={(params, event) => {
                  handleArrowKeyNavigation(params, event, push);
                  handleTabKeyNavigation(params, event, push);
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default CompanyContact;
